import React, { useState, useEffect, useRef, useMemo } from 'react'
import {useParams, useSearchParams} from 'react-router-dom';
import './searchObject.scss'
import AIPanel from "../../../components/AIChat/AIPanel";
import { useConvokeDomainContext } from '../ConvokeDomainProvider';
import {TableControl} from 'spark-core-dx/components';
import {useCoreRoute} from 'spark-core-dx/hooks'
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import ConvokeSourceObjectManager from '../../../managers/convoke/ConvokeSourceObjectManager';

const getPanelText = (value) => String(value ?? "").trim();

const parsePanelParams = (value) => {
    if (!value) {
        return {};
    }
    if (typeof value === "object" && !Array.isArray(value)) {
        return value;
    }
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }
    return {};
};

const getObjectPropCI = (obj, ...names) => {
    if (!obj || typeof obj !== "object") {
        return undefined;
    }
    const lookup = new Map(Object.keys(obj).map((key) => [String(key).toLowerCase(), key]));
    for (const name of names) {
        const key = lookup.get(String(name).toLowerCase());
        if (key !== undefined) {
            return obj[key];
        }
    }
    return undefined;
};

const parseMaybeJsonValue = (value) => {
    if (typeof value !== "string") {
        return value;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return value;
    }
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

const looksLikeFilterQuery = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    return getObjectPropCI(
        value,
        "SourceObjectId",
        "sourceObjectId",
        "RootFilterNode",
        "rootFilterNode",
        "SelectColumns",
        "selectColumns",
        "Skip",
        "skip",
        "Take",
        "take",
        "SortBy",
        "sortBy"
    ) !== undefined;
};

const resolveFilterQueryPayload = (payload, seen = new Set()) => {
    const parsed = parseMaybeJsonValue(payload);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
    }
    if (seen.has(parsed)) {
        return null;
    }
    seen.add(parsed);

    if (looksLikeFilterQuery(parsed)) {
        return parsed;
    }

    const args = getObjectPropCI(parsed, "args", "Args");
    const metadata = getObjectPropCI(parsed, "metadata", "Metadata");
    const candidates = [
        getObjectPropCI(metadata, "query", "Query"),
        getObjectPropCI(args, "query", "Query"),
        getObjectPropCI(args, "output", "Output"),
        getObjectPropCI(parsed, "query", "Query"),
        getObjectPropCI(parsed, "generatedQuery", "GeneratedQuery"),
        getObjectPropCI(parsed, "value", "Value"),
        getObjectPropCI(parsed, "output", "Output")
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate, seen);
        if (resolved) {
            return resolved;
        }
    }

    return null;
};

const resolvePanelFilterQuery = (item, value, sourceObjectId) => {
    const candidates = [
        value,
        item?.GeneratedQuery,
        item?.generatedQuery,
        item?.Value,
        item?.value,
        item?.Output,
        item?.output,
        item
    ];

    for (const candidate of candidates) {
        const resolved = resolveFilterQueryPayload(candidate);
        if (resolved) {
            const existingId = getObjectPropCI(resolved, "SourceObjectId", "sourceObjectId");
            return {
                ...resolved,
                SourceObjectId: existingId || sourceObjectId
            };
        }
    }

    return null;
};

const firstMetadataItem = (metadata) => {
    if (Array.isArray(metadata)) {
        return metadata.first?.() ?? metadata[0];
    }
    return metadata;
};

const toTextList = (value) => {
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
    }
    return [];
};

const SearchSourceObject = ({
    panelMode = false,
    panelOpen = true,
    panelId = null,
    panelSessionId = null,
    panelParams = null,
    onPanelClose = null
}) => {

    const [data, setData] = useState(null);
    const [fieldOverrides, setFieldOverrides] = useState(null);
    const {coreNavigate} = useCoreRoute();
    const domainContext = useConvokeDomainContext() ?? {};
    const coreContext = useCoreContext();
    const coreState = useCoreStateContext();
    const {soId: routeSoId} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const resolvedPanelParams = useMemo(() => parsePanelParams(panelParams), [panelParams]);
    const soId = panelMode
        ? (getPanelText(panelId) || getPanelText(searchParams.get("aiPanelId")) || routeSoId)
        : routeSoId;
    const panelSid = getPanelText(panelSessionId) || getPanelText(searchParams.get("aiPanelSessionId"));
    const fallbackSourceObjectMgr = useMemo(() => {
        if (domainContext.sourceObjectMgr || !soId) {
            return null;
        }
        return new ConvokeSourceObjectManager(soId, coreContext, coreState, "SearchSourceObject", true);
    }, [coreContext, coreState, domainContext.sourceObjectMgr, soId]);
    const sourceObjectMgr = domainContext.sourceObjectMgr ?? fallbackSourceObjectMgr;
    const loadingState = domainContext.loadingState ?? sourceObjectMgr?.loading?.All ?? false;
    const pageRef = useRef(null);
    const [initialSearch,setInitialSearch] = useState(false);
    const tableData = data ?? sourceObjectMgr?.filteredData ?? [];
    const selectedChatSession = useMemo(
        () => panelMode && panelSid ? { _id: panelSid } : null,
        [panelMode, panelSid]
    );

        useEffect(() => {
            if (!data && sourceObjectMgr?.uiMetadata && !initialSearch) {
                setInitialSearch(true);
                const metadata = sourceObjectMgr?.uiMetadata?.length == undefined ? sourceObjectMgr?.uiMetadata : sourceObjectMgr?.uiMetadata?.first();
                let take = metadata?.ProfilePreferences?.HasPaging ? metadata?.ProfilePreferences?.HasPaging.first() : metadata?.HasPaging ? metadata?.HasPaging.first() : 50000;
                const isServerSide = metadata?.ProfilePreferences?.ServerFilterSort !== undefined && metadata?.ProfilePreferences?.ServerFilterSort !== null   ? 
                                     metadata?.ProfilePreferences?.ServerFilterSort : metadata?.ServerFilterSort;
                sourceObjectMgr?.search({id:soId,queryParams:{search:true}, take: (isServerSide ? take : 50000),forceUpdate:true });
            }
            else if (sourceObjectMgr && !sourceObjectMgr?.uiMetadata){
                
                sourceObjectMgr?.LoadMetadata();
            }
        },[sourceObjectMgr?.uiMetadata])
    

    useEffect(() => {
        if (!loadingState && sourceObjectMgr) {
            setData(sourceObjectMgr?.filteredData)
        }
    }, [loadingState, sourceObjectMgr?.filteredData])

    const onServerSideQuery = async (itemQuery) => {
       const result = await sourceObjectMgr.search({id:soId,queryParams:{search:true}, searchModel: itemQuery, forceUpdate: true })
       return result;
    }

    const onSessionChange = (sessionId) => {
        if (!panelMode) {
            return;
        }

        const nextParams = new URLSearchParams(searchParams);
        const normalizedSessionId = getPanelText(sessionId);
        if (normalizedSessionId) {
            nextParams.set("aiPanelSessionId", normalizedSessionId);
        } else {
            nextParams.delete("aiPanelSessionId");
        }
        setSearchParams(nextParams);
    }

    const onUpdatePanel = async (UICommand, item, value)=>{
        if(UICommand === "filter"){
              const filterQuery = resolvePanelFilterQuery(item, value, soId);
              if (!filterQuery) return;

              const selectColumns = toTextList(getObjectPropCI(filterQuery, "SelectColumns", "selectColumns"))
              const deepCopy = v => typeof structuredClone === "function" ? structuredClone(v) : JSON.parse(JSON.stringify(v)); // fallback
              const metadataItem = firstMetadataItem(sourceObjectMgr?.uiMetadata);
              const metadataFields = Array.isArray(metadataItem?.Fields) ? metadataItem.Fields : [];
                if (selectColumns && selectColumns.length > 0 && metadataFields.length > 0) {
                    const allowed = new Set(selectColumns.map(s => s.toLowerCase()));
                    let fieldToOverride = metadataFields.map(deepCopy);
                    // Flip ShowOnGrid on the copies only
                    fieldToOverride = fieldToOverride.map(f => {
                        const name = (f?.FieldName ?? "").toString().toLowerCase();
                        if (!allowed.has(name)) {
                            f.ShowOnGrid = false;
                            f.IsActive = false;
                            f.IsDeleted = true;
                        }
                        return f;
                    });

                    setFieldOverrides(fieldToOverride);
                }

            const filterSourceObjectId = getObjectPropCI(filterQuery, "SourceObjectId", "sourceObjectId") ?? soId;
            const result = await sourceObjectMgr.search({id:filterSourceObjectId, searchModel: filterQuery,queryParams:{search:true}, forceUpdate: true })
            if (Array.isArray(result?.Items)) {
                setData(result.Items);
            }
        }
    }
    
    const onLoadRow = async (rowId) => {
        const item = await sourceObjectMgr.getItem(rowId).then((r) => {
            if (r.Success) {
                return r.Items.first()
            }
        })
        const newData = tableData.map(i => i._id === item._id ? item : i)
        setData(newData)
        return item
    }

    return ( <>
                    {sourceObjectMgr?.uiMetadata?.AISupportsSession || true ?
            		<AIPanel
						childrenRef={pageRef}
						open={panelOpen}
                        presentation={panelMode ? "slideout" : "inline"}
						onUpdatePanel={onUpdatePanel}
                        onSessionChange={panelMode ? onSessionChange : undefined}
                        session={selectedChatSession}
						mapToClass={'core-layout'}
                        params={resolvedPanelParams}
						referenceId={soId}
                        referenceName={"object"}
                        operationName = {"AIQuestionSourceObject"}
                        directModeOnly={true}
						onCancel={()=>{
                            if (panelMode && typeof onPanelClose === "function") {
                                onPanelClose();
                            } else {
                                coreNavigate("/dynamic/sourceobject/");
                            }
                        }}
                        >
                            <div className="ai-panel__table">
                                <TableControl
                                    data={tableData}
                                    className={"search-object-table"}
                                    enableMultiSelect={false}
                                    defaultNumRows={50}
                                    onServerSideQuery={onServerSideQuery}
                                    metadata={sourceObjectMgr?.uiMetadata}
                                    loadingState={loadingState}
                                    filters={sourceObjectMgr?.filters}
                                    mgr={sourceObjectMgr}
                                    fieldOverrides = {fieldOverrides}
                                    enableSidePanelEdit={true}
                                    onLoadRow={onLoadRow}
                                />
                            </div>
					</AIPanel> :
                     <TableControl
                                data={tableData}
                                className={"search-object-table"}
                                enableMultiSelect={false}
                                fieldOverrides = {fieldOverrides}
                                defaultNumRows={50}
                                onServerSideQuery={onServerSideQuery}
                                metadata={sourceObjectMgr?.uiMetadata}
                                loadingState={loadingState}
                                filters={sourceObjectMgr?.filters}
                                mgr={sourceObjectMgr}
                                enableSidePanelEdit={true}
                            />}
           
        </>
    );
}

export default SearchSourceObject;



